import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { FileText, PenTool, Sparkles, ArrowRight, X } from 'lucide-react';

const STORAGE_KEY = (userId: string) => `onboarding_v1_${userId}`;

export function useOnboardingDone() {
  const { user } = useAuth();
  if (!user) return { isDone: true, markDone: () => {} };
  const key = STORAGE_KEY(user.id);
  const isDone = localStorage.getItem(key) === '1';
  const markDone = () => localStorage.setItem(key, '1');
  return { isDone, markDone };
}

// ─── Step data ────────────────────────────────────────────────────────────────

const STEPS = [
  {
    icon: <Sparkles size={36} className="text-primary" />,
    bg: 'bg-primary/10',
    title: {
      fr: 'Bienvenue sur Consentink 👋',
      en: 'Welcome to Consentink 👋',
      es: '¡Bienvenido a Consentink! 👋',
    },
    body: {
      fr: 'Remplacez vos formulaires papier par des consentements numériques signés en moins de 2 minutes — même sans WiFi.',
      en: 'Replace paper forms with digital consent signed in under 2 minutes — even without WiFi.',
      es: 'Reemplaza los formularios en papel por consentimientos digitales firmados en menos de 2 minutos — sin WiFi.',
    },
    cta: { fr: 'Commencer →', en: 'Get started →', es: 'Empezar →' },
    skip: true,
  },
  {
    icon: <FileText size={36} className="text-primary" />,
    bg: 'bg-primary/10',
    title: {
      fr: 'Étape 1 — Créez votre modèle',
      en: 'Step 1 — Create your template',
      es: 'Paso 1 — Crea tu plantilla',
    },
    body: {
      fr: 'Consentink inclut 5 modèles prêts à l\'emploi (tatouage, piercing, microblading…). Personnalisez-les ou créez les vôtres avec l\'éditeur visuel.',
      en: 'Consentink includes 5 ready-to-use templates (tattoo, piercing, microblading…). Customise them or build your own with the visual editor.',
      es: 'Consentink incluye 5 plantillas listas para usar (tatuaje, piercing, microblading…). Personalízalas o crea las tuyas con el editor visual.',
    },
    cta: { fr: 'Voir mes modèles', en: 'View my templates', es: 'Ver mis plantillas' },
    ctaRoute: '/templates',
    secondaryCta: { fr: 'Plus tard', en: 'Later', es: 'Más tarde' },
    skip: false,
  },
  {
    icon: <PenTool size={36} className="text-primary" />,
    bg: 'bg-primary/10',
    title: {
      fr: 'Étape 2 — Faites signer',
      en: 'Step 2 — Get it signed',
      es: 'Paso 2 — Haz que lo firmen',
    },
    body: {
      fr: 'Sélectionnez un modèle, faites remplir le formulaire par votre client sur la tablette, et il signe en direct. Le PDF est archivé et envoyé par email automatiquement.',
      en: 'Select a template, have your client fill in the form on the tablet, and they sign live. The PDF is archived and emailed automatically.',
      es: 'Selecciona una plantilla, haz que tu cliente complete el formulario en la tableta y firme en directo. El PDF se archiva y se envía por email automáticamente.',
    },
    cta: { fr: 'Créer mon premier contrat', en: 'Create my first contract', es: 'Crear mi primer contrato' },
    ctaRoute: '/templates',
    secondaryCta: { fr: 'Aller au tableau de bord', en: 'Go to dashboard', es: 'Ir al panel' },
    skip: false,
    isLast: true,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  onDone: () => void;
}

export function OnboardingFlow({ onDone }: Props) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const handleCta = () => {
    if (current.ctaRoute) {
      onDone();
      navigate(current.ctaRoute);
    } else {
      if (isLast) {
        onDone();
      } else {
        setStep(s => s + 1);
      }
    }
  };

  const handleSecondaryCta = () => {
    if (isLast) {
      onDone();
    } else {
      setStep(s => s + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-6">
      {/* Skip button */}
      <button
        onClick={onDone}
        className="absolute top-5 right-5 p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        aria-label="Fermer"
      >
        <X size={20} />
      </button>

      {/* Step dots */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 flex gap-2">
        {STEPS.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === step ? 'w-6 bg-primary' : 'w-1.5 bg-border'
            }`}
          />
        ))}
      </div>

      {/* Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.22 }}
          className="max-w-sm w-full text-center space-y-6"
        >
          {/* Icon */}
          <div className={`w-20 h-20 rounded-3xl ${current.bg} flex items-center justify-center mx-auto`}>
            {current.icon}
          </div>

          {/* Text */}
          <div className="space-y-3">
            <h2 className="text-2xl font-bold font-display leading-tight">
              {current.title[language as 'fr' | 'en' | 'es']}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {current.body[language as 'fr' | 'en' | 'es']}
            </p>
          </div>

          {/* CTAs */}
          <div className="space-y-3">
            <button
              onClick={handleCta}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-2xl font-semibold text-base hover:bg-primary/90 transition-colors"
            >
              {current.cta[language as 'fr' | 'en' | 'es']}
              {!current.ctaRoute && <ArrowRight size={16} />}
            </button>

            {current.secondaryCta && (
              <button
                onClick={handleSecondaryCta}
                className="w-full py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {current.secondaryCta[language as 'fr' | 'en' | 'es']}
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
